import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import type { SetBudgetsInput } from '@accounting/shared';
import { useBudgetGrid, useImportBudget, useSetBudgets } from '../../hooks/useBudgets';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { FileDropZone } from '../../components/ui/FileDropZone';
import { formatCurrency } from '../../lib/formatters';
import { ApiError } from '../../lib/api';

export const Route = createFileRoute('/_auth/budget/')({
  component: BudgetPage,
});

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];
type Mode = 'monthly' | 'quarterly' | 'yearly';

interface Row {
  accountId: string;
  code: string;
  name: string;
  months: string[]; // 12, source of truth
}

/** Split a total evenly into n parts (2dp) that sum back to the total exactly. */
function spreadEvenly(total: number, n: number): string[] {
  const cents = Math.round(total * 100);
  const base = Math.trunc(cents / n);
  const rem = cents - base * n;
  return Array.from({ length: n }, (_, i) => ((base + (i < rem ? 1 : 0)) / 100).toFixed(2));
}

const sum = (xs: string[]) => xs.reduce((s, x) => s + (parseFloat(x) || 0), 0);

/** A cell that edits locally and commits on blur/Enter (so spreads don't fire per keystroke). */
function BudgetCell({ value, onCommit }: { value: string; onCommit: (v: string) => void }) {
  const display = parseFloat(value) ? String(parseFloat(value)) : '';
  const [draft, setDraft] = useState(display);
  useEffect(() => setDraft(display), [display]);
  return (
    <input
      inputMode="decimal"
      value={draft}
      placeholder="0"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => onCommit(draft)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
      style={{ width: '100%', textAlign: 'right', padding: '4px 6px' }}
    />
  );
}

function BudgetPage() {
  const companyId = useActiveCompanyId();
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [mode, setMode] = useState<Mode>('monthly');
  const { data: grid, isLoading } = useBudgetGrid(companyId, year);
  const save = useSetBudgets(companyId);
  const importMut = useImportBudget(companyId);

  const [rows, setRows] = useState<Row[]>([]);
  useEffect(() => {
    if (grid) {
      setRows(grid.rows.map((r) => ({ accountId: r.accountId, code: r.code, name: r.name, months: [...r.months] })));
    }
  }, [grid]);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>Budget</h1>
        <p className="muted">No active company. Budgets are per-company.</p>
      </div>
    );
  }

  const setMonths = (ri: number, months: string[]) =>
    setRows((prev) => prev.map((r, i) => (i === ri ? { ...r, months } : r)));

  // Commit a value for a given column in the active mode, spreading as needed.
  const commit = (ri: number, col: number, raw: string) => {
    const v = parseFloat(raw) || 0;
    const r = rows[ri]!;
    if (mode === 'monthly') {
      setMonths(ri, r.months.map((m, j) => (j === col ? v.toFixed(2) : m)));
    } else if (mode === 'quarterly') {
      const parts = spreadEvenly(v, 3); // col = quarter index 0..3
      setMonths(ri, r.months.map((m, j) => (Math.floor(j / 3) === col ? parts[j % 3]! : m)));
    } else {
      setMonths(ri, spreadEvenly(v, 12)); // yearly
    }
  };

  const rowTotal = (r: Row) => sum(r.months);
  const quarterValue = (r: Row, q: number) => sum(r.months.slice(q * 3, q * 3 + 3));

  const onSave = () => {
    const entries: SetBudgetsInput['entries'] = [];
    for (const r of rows) {
      r.months.forEach((m, idx) => {
        const v = parseFloat(m) || 0;
        if (v > 0) entries.push({ accountId: r.accountId, month: idx + 1, amount: String(v) });
      });
    }
    save.mutate({ year, entries });
  };

  const onFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = String(reader.result).split(',')[1] ?? '';
      importMut.mutate({ year, fileName: file.name, content: base64 });
    };
    reader.readAsDataURL(file);
  };

  const importError =
    importMut.error instanceof ApiError
      ? (importMut.error.body as { error?: string } | null)?.error ?? 'Import failed'
      : importMut.error?.message;
  const result = importMut.data;

  const columns = mode === 'monthly' ? MONTHS : mode === 'quarterly' ? QUARTERS : ['Yearly'];
  const colWidth = mode === 'monthly' ? 78 : mode === 'quarterly' ? 120 : 160;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, margin: 0 }}>Budget</h1>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
            Budget any active account. Enter by month, quarter, or a single yearly amount — quarter
            and yearly entries spread evenly across the underlying months.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {(['monthly', 'quarterly', 'yearly'] as Mode[]).map((m) => (
              <button
                key={m}
                className={mode === m ? 'btn-primary' : 'btn-secondary'}
                onClick={() => setMode(m)}
                style={{ textTransform: 'capitalize' }}
              >
                {m}
              </button>
            ))}
          </div>
          <label style={{ fontSize: 13 }}>
            Year{' '}
            <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {[thisYear - 1, thisYear, thisYear + 1].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </label>
          <Link to="/reports/budget-vs-actual" className="btn-secondary" style={{ textDecoration: 'none' }}>Budget vs Actual</Link>
          <button className="btn-primary" onClick={onSave} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save budget'}
          </button>
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <FileDropZone
          accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          busy={importMut.isPending}
          hint="Import an Excel budget (.xlsx): column A = account code, columns Jan…Dec"
          onFiles={(files) => onFile(files[0]!)}
        />
      </div>

      {importError && <div className="field-error" style={{ marginBottom: 12 }}>{importError}</div>}
      {result && (
        <div className="card" style={{ padding: 12, marginBottom: 12 }}>
          Imported {result.imported} cell(s) for {result.year}.
          {result.unknownCodes.length > 0 && <span className="field-error"> Unknown codes: {result.unknownCodes.join(', ')}.</span>}
          {result.errors.length > 0 && <span className="field-error"> {result.errors.length} row error(s): {result.errors.slice(0, 3).map((e) => e.message).join('; ')}{result.errors.length > 3 ? '…' : ''}</span>}
        </div>
      )}

      {isLoading && <p className="muted">Loading…</p>}
      {!isLoading && rows.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <p className="muted" style={{ marginTop: 0 }}>No active accounts to budget. Add accounts first.</p>
        </div>
      )}

      {rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="table" style={{ minWidth: mode === 'monthly' ? 1100 : 600 }}>
            <thead>
              <tr>
                <th style={{ position: 'sticky', left: 0, background: 'var(--bg-surface)' }}>Account</th>
                {columns.map((c) => (
                  <th key={c} style={{ textAlign: 'right', width: colWidth }}>{c}</th>
                ))}
                <th style={{ textAlign: 'right', width: 110 }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={r.accountId}>
                  <td style={{ position: 'sticky', left: 0, background: 'var(--bg-surface)', whiteSpace: 'nowrap' }}>
                    {r.code} — {r.name}
                  </td>
                  {mode === 'monthly' &&
                    r.months.map((m, mi) => (
                      <td key={mi}>
                        <BudgetCell value={m} onCommit={(v) => commit(ri, mi, v)} />
                      </td>
                    ))}
                  {mode === 'quarterly' &&
                    QUARTERS.map((_, qi) => (
                      <td key={qi}>
                        <BudgetCell value={quarterValue(r, qi).toFixed(2)} onCommit={(v) => commit(ri, qi, v)} />
                      </td>
                    ))}
                  {mode === 'yearly' && (
                    <td>
                      <BudgetCell value={rowTotal(r).toFixed(2)} onCommit={(v) => commit(ri, 0, v)} />
                    </td>
                  )}
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(rowTotal(r).toFixed(2))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
